import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { NavLink, type To } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { theme } from '@/@bothrs-theme/theme'
import { Flex, Padding } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { toRem } from '@/config/layout'
import { LogOutAction } from '@/modules/auth/components/log-out-action'
import { CypressTestId } from '@/modules/cypress/components/cypress-test-id/CypressTestId'
import { useSwitchHospital } from '@/modules/shared/hooks/useSwitchHospital'
import { useFeatureFlag } from '@/utils/feature-flag'
import { HospitalIndicator } from '../hospital-indicator/HospitalIndicator'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { FC, MouseEvent } from 'react'

export type WebNavigationItem = {
  href: To // Changed from Path | string to To for broader compatibility
  label: string
  icon: IconProp
  iconActive?: IconProp
  id?: string // Added for consistency if needed, though key is usually href
  onClick?: (event?: React.MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => void
  badgeCount?: number
  hasNotification?: boolean
}

export type WebNavigationProps = {
  items: WebNavigationItem[]
  appVersion?: string // Making app version a prop for clarity
}

export const WebNavigation: FC<WebNavigationProps> = ({ items, appVersion = '1.0.0' }) => {
  const switchHospital = useSwitchHospital()
  const olvw = useFeatureFlag('tenant_olvw') === '1'
  const versionToDisplay = typeof APP_VERSION === 'undefined' ? appVersion : APP_VERSION

  return (
    <StyledSideBarContainer>
      <Flex alignItems='center' justifyContent='center' flexDirection='column'>
        <Padding pl={theme.UI.Spacing.Space3} pr={theme.UI.Spacing.Space3}>
          <StyledLogo src='/img/logo.png' alt={t({ id: 'alt.logo', message: 'Logo van MijnZiekenhuis' })} />
        </Padding>
      </Flex>
      {items.map((item) => (
        <StyledNavigationItem
          key={typeof item.href === 'string' ? item.href : item.href.pathname || 'key'} // Adjusted key for To type
          to={item.href} // Now directly accepts To
          replace
          onClick={item.onClick}
        >
          {({ isActive }: { isActive: boolean }) => (
            <>
              <StyledNavigationIconContainer>
                <FontAwesomeIcon icon={isActive ? (item.iconActive ?? item.icon) : item.icon} />
                {item.badgeCount !== undefined && item.badgeCount > 0 && (
                  <StyledBadge>{item.badgeCount > 99 ? '99' : item.badgeCount}</StyledBadge>
                )}

                {item.hasNotification && <StyledNotificationDot />}
              </StyledNavigationIconContainer>
              <Span type='BodySmallRegular'>{item.label}</Span>
            </>
          )}
        </StyledNavigationItem>
      ))}
      <Flex flexGrow={1} />
      <Padding px={theme.UI.Spacing.Space4} pb={theme.UI.Spacing.Space2} style={{ width: '100%' }}>
        <HospitalIndicator variant='sidebar' />
      </Padding>
      {olvw ? (
        <CypressTestId id='switch-tenant'>
          <StyledExternalNavigationItem
            href='/select-hospital'
            onClick={(event_) => {
              event_.preventDefault()
              switchHospital()
            }}
          >
            <FontAwesomeIcon icon={['fal', 'arrows-repeat']} size='lg' />
            <Span type='BodySmallRegular'>
              {t({
                id: 'profile.account-settings.switch-tenant',
                message: 'Wisselen van ziekenhuis',
              })}
            </Span>
          </StyledExternalNavigationItem>
        </CypressTestId>
      ) : null}
      <CypressTestId id='log-out-button'>
        <LogOutAction>
          <StyledNavigationItem
            key='log-out'
            to='/log-out' // This path might need to be dynamic or configured
            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.preventDefault()} // Typed event
          >
            <FontAwesomeIcon icon={['fal', 'arrow-right-from-bracket']} />
            <Span type='BodySmallRegular'>
              {t({
                id: 'profile.account-settings.log-out',
                message: 'Afmelden',
              })}
            </Span>
          </StyledNavigationItem>
        </LogOutAction>
      </CypressTestId>
      {versionToDisplay && <StyledNavigationLabel>v{versionToDisplay}</StyledNavigationLabel>}
    </StyledSideBarContainer>
  )
}

const StyledSideBarContainer = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  background-color: ${({ theme }) => theme.Colors.Background.White};
  box-shadow:
    0px 0px 70px rgba(205, 208, 213, 0.15),
    0px 0px 10px rgba(170, 179, 193, 0.12);
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

// Renamed from StLogo to StyledLogo for consistency with the new component
const StyledLogo = styled.img`
  max-height: 60px;
  align-self: center;
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space6};
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const navigationItemStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  user-select: none;
  text-align: left;
  position: relative;
  overflow: hidden;
  width: 100%;
  z-index: 1;

  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4};
    margin: ${theme.UI.SpacingPx.Space1} 0;
    color: ${theme.Components.Navigation.Text};
    transition: color 175ms ease-in-out;

    &:hover {
      color: ${theme.Components.Navigation.ActiveText};
    }

    &.active {
      color: ${theme.Components.Navigation.ActiveText};

      > * {
        z-index: 1;
      }

      span {
        font-weight: 700;
      }

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        z-index: 0;
        width: 100%;
        height: 100%;
        background-color: ${theme.Colors.Neutrals.Lighter};
        animation: zoomIn; // Assuming zoomIn animation is defined globally or in theme
        animation-duration: 150ms;
      }
    }
  `}

  svg {
    margin-right: 10px;
    margin-bottom: 0;
    width: 20px;
    height: 20px;
  }
`

const StyledNavigationItem = styled(NavLink)`
  ${navigationItemStyles}
`

const StyledExternalNavigationItem = styled.a`
  ${navigationItemStyles}
`

const StyledNavigationLabel = styled.p`
  color: ${({ theme }) => theme.Components.Navigation.Text};
  font-size: ${toRem(12)};
  padding-left: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-right: ${({ theme }) => theme.UI.SpacingPx.Space4};
  margin-top: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StyledNavigationIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StyledBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -2px;
  background-color: ${({ theme }) => theme.Colors.Status.Error};
  color: ${({ theme }) => theme.Colors.Background.White};
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  min-width: 20px;
  height: 20px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.Colors.Neutrals.Lighter};
  z-index: 2;
`

const StyledNotificationDot = styled.div`
  position: absolute;
  top: 0px;
  right: 6px;
  width: 8px;
  height: 8px;
  background-color: ${({ theme }) => theme.Colors.Status.Error};
  border-radius: 50%;
`
